import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { AlertTriangle, ExternalLink, Loader2, MapPin, Search, Tag, X } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const capitalizeStart = (value) => value.trim().replace(/\p{L}/u, (letter) => letter.toUpperCase());
const keywordTags = (values) => {
  const seen = new Set();
  return values.flatMap((value) => value.split(",")).map((value) => value.trim()).filter((value) => {
    const key = value.toLowerCase();
    if (!value || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map(capitalizeStart);
};
const validMapLink = (value) => {
  if (!value.trim()) return true;
  try { return ["http:", "https:"].includes(new URL(value.trim()).protocol); } catch { return false; }
};
const FieldLabel = ({ htmlFor, children, required = false }) => (
  <Label htmlFor={htmlFor} data-testid={`${htmlFor}-label`} className="block text-sm font-bold text-stone-700 mb-2">
    {children}{required && <span data-testid={`${htmlFor}-required`} aria-hidden="true" className="ml-1 text-rose-600">*</span>}
  </Label>
);

const MapLocation = ({ form, setForm, error }) => {
  const [mode, setMode] = useState(null);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(false);
  const [searchRequest, setSearchRequest] = useState(0);
  const [manualPrompt, setManualPrompt] = useState(false);
  const searchAbortRef = useRef(null);
  const searchInputRef = useRef(null);
  const manualInputRef = useRef(null);
  const mapLinkRef = useRef(null);
  const hasMapLink = !!form.map_url.trim() && validMapLink(form.map_url);
  const [search, setSearch] = useState({ loading: false, completed: false, error: "", results: [] });

  const clearSearch = useCallback(() => {
    searchAbortRef.current?.abort();
    setQuery("");
    setPicked(false);
    setSearchRequest(0);
    setSearch({ loading: false, completed: false, error: "", results: [] });
  }, []);

  const cancelSearch = () => {
    clearSearch();
    setManualPrompt(true);
  };

  const findFromForm = () => {
    setQuery([form.title, form.city, form.country].map((value) => value.trim().replace(/\s+/g, " ")).filter(Boolean).join(", ").slice(0, 120));
    setPicked(false);
    setMode("search");
    setSearchRequest((current) => current + 1);
  };

  useEffect(() => {
    setSearch({ loading: false, completed: false, error: "", results: [] });
    if (mode !== "search" || picked || query.trim().length < 2) return;
    const controller = new AbortController();
    searchAbortRef.current = controller;
    let ignore = false;
    const timer = setTimeout(async () => {
      setSearch({ loading: true, completed: false, error: "", results: [] });
      try {
        const { data } = await api.get("/places/search", { params: { q: query.trim() }, signal: controller.signal });
        if (!ignore && !controller.signal.aborted) {
          if (data.results.length) {
            setSearch({ loading: false, completed: true, error: "", results: data.results });
          } else {
            clearSearch();
            setManualPrompt(true);
          }
        }
      } catch {
        if (!ignore && !controller.signal.aborted) {
          setSearch({ loading: false, completed: true, error: "Place search is unavailable. You can enter the location manually or save without a map.", results: [] });
          setManualPrompt(true);
        }
      }
    }, 500);
    return () => { ignore = true; controller.abort(); clearTimeout(timer); };
  }, [query, mode, picked, searchRequest, clearSearch]);

  const choosePlace = (place) => {
    setForm((current) => ({ ...current, address: place.address || "", lat: place.lat, lon: place.lon, map_url: place.map_url || "", source: "osm" }));
    setPicked(true);
    setQuery(place.name);
    setSearch({ loading: false, completed: false, error: "", results: [] });
    setMode(null);
    requestAnimationFrame(() => mapLinkRef.current?.focus());
  };

  const finishManualEntry = (focusLink = false) => {
    if (!hasMapLink) return;
    setMode(null);
    if (focusLink) requestAnimationFrame(() => mapLinkRef.current?.focus());
  };

  return (
    <section data-testid="save-maps-section" className="min-w-0">
      {mode ? <FieldLabel htmlFor={mode === "search" ? "save-place-search" : "save-maplink-input"}>Maps link</FieldLabel> : <p data-testid="save-maps-label" className="text-sm font-bold text-stone-700 mb-2">Maps link</p>}
      <div className="flex flex-wrap gap-2 mb-3">
        <Button type="button" data-testid="save-mode-search" aria-pressed={mode === "search"} variant="outline" size="sm" onClick={findFromForm} className={`rounded-full text-xs ${mode === "search" ? "bg-ac-soft text-ac-softfg border-ac" : "text-stone-500"}`}>Find on OpenStreetMap</Button>
        <Button type="button" data-testid="save-mode-manual" aria-pressed={mode === "manual"} variant="outline" size="sm" onClick={() => setMode("manual")} className={`rounded-full text-xs ${mode === "manual" ? "bg-ac-soft text-ac-softfg border-ac" : "text-stone-500"}`}>Enter manually</Button>
      </div>
      {hasMapLink && <a ref={mapLinkRef} data-testid="save-map-link" href={form.map_url.trim()} target="_blank" rel="noopener noreferrer" aria-label="Open in Maps (opens in a new tab)" className="mb-3 inline-flex items-center gap-2 rounded text-sm font-bold text-ac underline underline-offset-4 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-opacity"><MapPin aria-hidden="true" className="w-4 h-4" />Open in Maps<ExternalLink aria-hidden="true" className="w-3.5 h-3.5" /></a>}
      {mode === "search" ? <>
        <div className="relative">
          <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input ref={searchInputRef} id="save-place-search" data-testid="save-place-search" aria-label="OpenStreetMap search" maxLength={120} value={query} onChange={(event) => { setQuery(event.target.value); setPicked(false); }} onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }} className="rounded-xl pl-9 pr-20" />
          {search.loading && <Loader2 data-testid="save-place-search-loading" aria-label="Searching places" className="absolute right-11 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-stone-400" />}
          <button type="button" data-testid="save-place-search-cancel" aria-label="Cancel place search" title="Cancel search" onClick={cancelSearch} className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-full text-stone-500 hover:bg-stone-100 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"><X aria-hidden="true" className="w-4 h-4" /></button>
        </div>
        {search.results.length > 0 && <div data-testid="save-place-results" className="mt-2 border border-stone-200 rounded-2xl overflow-hidden max-h-48 overflow-y-auto">
          {search.results.map((place, index) => <button type="button" key={index} data-testid={`save-place-result-${index}`} onClick={() => choosePlace(place)} className="w-full text-left px-3 py-2.5 hover:bg-stone-50 border-b border-stone-100 last:border-0 transition-colors">
            <span data-testid={`save-place-result-name-${index}`} className="block font-semibold text-sm break-words">{place.name}</span>
            <span data-testid={`save-place-result-address-${index}`} className="block text-xs text-stone-500 break-words">{place.address || [place.city, place.country].filter(Boolean).join(", ")}</span>
          </button>)}
        </div>}
        {searchRequest > 0 && query.trim().length < 2 && <p data-testid="save-place-search-hint" role="status" className="mt-2 text-xs text-stone-500">Add a place name or location above, then choose Find on OpenStreetMap. You can also enter a maps link manually.</p>}
        {search.error && <p data-testid="save-place-search-error" role="status" className="mt-2 text-xs text-rose-600">{search.error}</p>}
        {search.completed && !search.error && !search.results.length && <p data-testid="save-place-search-empty" role="status" className="mt-2 text-xs text-stone-500">No places found.</p>}
        <p data-testid="save-place-attribution" className="mt-2 text-xs text-stone-400">© OpenStreetMap contributors · Search via Photon</p>
      </> : mode === "manual" ? <>
        <Input ref={manualInputRef} id="save-maplink-input" data-testid="save-maplink-input" type="url" inputMode="url" placeholder="Paste a maps link" value={form.map_url} onChange={(event) => setForm((current) => ({ ...current, map_url: event.target.value, lat: null, lon: null, address: "", source: "manual" }))}           onBlur={(event) => { if (!event.relatedTarget?.closest("button")) finishManualEntry(); }}
          onKeyDown={(event) => { if (event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); finishManualEntry(true); } }}
          aria-invalid={!!error} aria-describedby={error ? "save-maplink-error" : undefined} className="rounded-xl" />
        <p data-testid="save-manual-location-hint" className="mt-2 text-xs text-stone-500">Use the city and country fields above for the location. A maps link is not required.</p>
      </> : null}
      {error && <p id="save-maplink-error" data-testid="save-maplink-error" role="alert" className="mt-2 text-xs text-rose-600">{error}</p>}
      <AlertDialog open={manualPrompt} onOpenChange={setManualPrompt}>
        <AlertDialogContent data-testid="save-manual-link-dialog" aria-describedby={undefined} onEscapeKeyDown={(event) => event.stopPropagation()} onCloseAutoFocus={(event) => { event.preventDefault(); (mode === "manual" ? manualInputRef : searchInputRef).current?.focus(); }} className="w-[calc(100%-2rem)] max-w-sm rounded-3xl font-['Figtree']">
          <AlertDialogHeader className="text-left">
            <AlertDialogTitle data-testid="save-manual-link-question" className="font-['Nunito']">Would you like to enter the maps link manually?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2 sm:space-x-0">
            <AlertDialogAction type="button" data-testid="save-manual-link-yes" onClick={() => setMode("manual")} className="flex-1 rounded-full bg-ac hover:bg-ac-hover text-white font-bold">Yes</AlertDialogAction>
            <AlertDialogCancel type="button" data-testid="save-manual-link-later" onClick={clearSearch} className="mt-0 flex-1 rounded-full font-bold">Later</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

const KeywordsField = ({ values, setValues, draft, setDraft, inputRef, error, required }) => {
  const commit = (value) => { setValues(keywordTags([...values, value])); setDraft(""); };
  const change = (value, composing) => {
    if (composing || !value.includes(",")) { setDraft(value); return; }
    const parts = value.split(",");
    setDraft(parts.pop());
    setValues(keywordTags([...values, ...parts]));
  };
  return (
    <div className="min-w-0">
      <FieldLabel htmlFor="save-keyword-input" required={required}>Keywords</FieldLabel>
      <div className="relative">
        <Tag aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input ref={inputRef} id="save-keyword-input" data-testid="save-keyword-input" value={draft} autoCapitalize="sentences" enterKeyHint="done" aria-required={required} aria-invalid={!!error} aria-describedby={error ? "save-keywords-error save-keywords-hint" : "save-keywords-hint"} onChange={(event) => change(event.target.value, event.nativeEvent.isComposing)} onCompositionEnd={(event) => change(event.currentTarget.value, false)} onBlur={() => { if (draft.trim()) commit(draft); }} onKeyDown={(event) => { if (event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); commit(draft); } }} className="rounded-xl pl-9" />
      </div>
      {!!values.length && <div data-testid="save-keyword-chips" className="flex flex-wrap gap-2 mt-2">
        {values.map((keyword) => <span key={keyword} data-testid={`save-keyword-chip-${keyword}`} className="inline-flex max-w-full items-start gap-1 bg-ac-soft text-ac-softfg text-xs font-bold px-2.5 py-1 rounded-full">
          <span className="min-w-0 break-words">{keyword}</span>
          <button type="button" data-testid={`save-keyword-remove-${keyword}`} aria-label={`Remove keyword ${keyword}`} onClick={() => setValues(values.filter((value) => value !== keyword))} className="shrink-0 rounded-full hover:bg-ac/10 transition-colors"><X className="w-3 h-3" /></button>
        </span>)}
      </div>}
      <p id="save-keywords-hint" data-testid="save-keywords-hint" className="mt-2 text-xs text-stone-500">{required ? "Add at least one keyword. Comma or Enter creates a tag." : "Comma or Enter creates a tag."}</p>
      {error && <p id="save-keywords-error" data-testid="save-keywords-error" role="alert" className="mt-1 text-xs text-rose-600">{error}</p>}
    </div>
  );
};

const DuplicateWarning = ({ matches, saving, onBack, onConfirm }) => (
  <section data-testid="save-duplicate-warning" className="space-y-4 rounded-2xl border border-ac bg-ac-soft p-4 min-w-0">
    <div role="alert" data-testid="save-duplicate-message">
      <h3 className="flex items-start gap-2 font-['Nunito'] font-bold text-ac-softfg"><AlertTriangle className="w-5 h-5 shrink-0" /> This place may already be saved</h3>
      <p className="mt-2 text-sm text-stone-600">These saves have the same place name and city. Review them, adjust your entry, or save anyway. Nothing will be merged or deleted.</p>
    </div>
    <div className="space-y-2">
      {matches.map((match) => <details key={match.id} data-testid={`duplicate-review-${match.id}`} className="rounded-xl border border-stone-200 bg-white p-3 min-w-0">
        <summary data-testid={`duplicate-review-toggle-${match.id}`} className="cursor-pointer font-bold text-sm text-stone-800 break-words rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Review {match.title}</summary>
        <div className="mt-3 space-y-2 text-sm text-stone-600 break-words">
          <p data-testid={`duplicate-review-location-${match.id}`}>{[match.city, match.country].filter(Boolean).join(", ") || match.address || ""}</p>
          {!!match.keywords?.length && <p data-testid={`duplicate-review-keywords-${match.id}`}>{match.keywords.join(" · ")}</p>}
          {match.notes && <p data-testid={`duplicate-review-notes-${match.id}`} className="whitespace-pre-wrap">{match.notes}</p>}
          {match.image_path && <img data-testid={`duplicate-review-cover-${match.id}`} loading="lazy" src={fileUrl(match.image_path)} alt={match.title} className="w-full h-32 object-cover rounded-xl" />}
          {match.map_url && validMapLink(match.map_url) && <a data-testid={`duplicate-review-map-${match.id}`} href={match.map_url} target="_blank" rel="noreferrer" className="inline-block font-bold text-ac underline">Open in Maps</a>}
        </div>
      </details>)}
    </div>
    <fieldset disabled={saving} className="grid grid-cols-2 gap-2 min-w-0">
      <Button type="button" data-testid="save-duplicate-back" onClick={onBack} variant="outline" className="rounded-full font-bold">Go back</Button>
      <Button type="button" data-testid="save-duplicate-confirm" onClick={onConfirm} className="rounded-full bg-ac hover:bg-ac-hover text-white font-bold">{saving ? "Saving…" : "Save anyway"}</Button>
    </fieldset>
  </section>
);

export const SavePlaceDialog = forwardRef(function SavePlaceDialog({ onSaved, initialItem = null, onCancel }, closeRef) {
  const initial = useRef({
    title: initialItem?.title ?? "", city: initialItem?.city ?? "", country: initialItem?.country ?? "",
    address: initialItem?.address ?? "", lat: initialItem?.lat ?? null, lon: initialItem?.lon ?? null,
    map_url: initialItem?.map_url ?? "", source: initialItem?.source ?? "manual",
    keywords: [...(initialItem?.keywords || [])], notes: initialItem?.notes ?? "", image_path: initialItem?.image_path ?? null,
  });
  const [form, setForm] = useState(initial.current);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [discardAction, setDiscardAction] = useState(null);
  const savedRef = useRef(false);
  const [errors, setErrors] = useState({});
  const [duplicate, setDuplicate] = useState(null);
  const warningRef = useRef(null);
  const titleRef = useRef(null);
  const cityRef = useRef(null);
  const countryRef = useRef(null);
  const keywordRef = useRef(null);
  const setKeywords = (keywords) => { setForm((current) => ({ ...current, keywords })); setErrors((current) => ({ ...current, keywords: undefined })); };
  const changed = new Set(Object.keys(form).filter((key) => JSON.stringify(form[key]) !== JSON.stringify(initial.current[key])));
  if (draft.trim()) changed.add("keywords");
  if (initialItem && !changed.has("map_url")) ["lat", "lon", "address", "source"].forEach((key) => changed.delete(key));
  const hasChanges = changed.size > 0 || uploading;
  const requestClose = (close) => {
    if (saving) return;
    if (hasChanges && !savedRef.current) setDiscardAction(() => close);
    else close();
  };
  useImperativeHandle(closeRef, () => ({ requestClose }));

  useEffect(() => {
    if (!hasChanges) return;
    const warnBeforeLeaving = (event) => {
      if (savedRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [hasChanges]);

  useEffect(() => {
    if (duplicate) {
      warningRef.current?.focus({ preventScroll: true });
      warningRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [duplicate]);

  const persist = async (payload, allowDuplicate = false) => {
    setSaving(true);
    setErrors((current) => ({ ...current, save: undefined }));
    try {
      const options = { params: { allow_duplicate: allowDuplicate } };
      const response = initialItem
        ? await api.put(`/saves/${initialItem.id}`, payload, options)
        : await api.post("/saves", payload, options);
      savedRef.current = true;
      onSaved(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === "duplicate_save") {
        setDuplicate({ matches: detail.matches, payload });
      } else {
        setErrors((current) => ({ ...current, save: "Couldn't save this place. Your entries are still here; please try again." }));
      }
    } finally { setSaving(false); }
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving || uploading || duplicate) return;
    const keywords = keywordTags([...form.keywords, draft]);
    const validation = {};
    if ((!initialItem || changed.has("title")) && !form.title.trim()) validation.title = "Enter a place name.";
    if (!form.city.trim()) validation.city = "Enter a city.";
    if (!form.country.trim()) validation.country = "Enter a country.";
    if ((!initialItem || changed.has("map_url")) && !validMapLink(form.map_url)) validation.map_url = "Enter a valid http or https maps link, or leave it empty.";
    setErrors(validation);
    if (Object.keys(validation).length) {
      if (validation.title) titleRef.current?.focus();
      else if (validation.city) cityRef.current?.focus();
      else if (validation.country) countryRef.current?.focus();
      else if (validation.keywords) keywordRef.current?.focus();
      return;
    }
    if (initialItem && !changed.size) { savedRef.current = true; onSaved(initialItem); return; }
    const city = capitalizeStart(form.city);
    const country = capitalizeStart(form.country);
    const prepared = { ...form, title: form.title.trim(), city, country, map_url: form.map_url.trim(), keywords, notes: form.notes };
    if (!initialItem && form.source !== "osm") prepared.address = [city, country].filter(Boolean).join(", ");
    const payload = initialItem ? Object.fromEntries([...changed].map((key) => [key, prepared[key]])) : prepared;
    await persist(payload);
  };

  return (
    <DialogContent data-testid={initialItem ? "save-edit-dialog" : "save-create-dialog"} aria-describedby={initialItem ? undefined : "save-form-required-description"} className="rounded-3xl font-['Figtree'] w-[calc(100%-2rem)] max-w-lg min-w-0 max-h-[88vh] overflow-y-auto">
      <DialogHeader className="text-left pr-5">
        <DialogTitle className="font-['Nunito'] text-2xl">{initialItem ? "Edit place" : "Save a place"}</DialogTitle>
        {!initialItem && <DialogDescription id="save-form-required-description" data-testid="save-required-hint">Fields marked with * are required.</DialogDescription>}
      </DialogHeader>
      <form data-testid="save-place-form" onSubmit={save} noValidate className="min-w-0">
        <fieldset hidden={!!duplicate} disabled={saving || !!duplicate} className="space-y-5 min-w-0">
          <div>
            <FieldLabel htmlFor="save-title-input" required>Place name</FieldLabel>
            <Input ref={titleRef} id="save-title-input" data-testid="save-title-input" placeholder="Name of the place" value={form.title} onChange={(event) => { setForm((current) => ({ ...current, title: event.target.value })); setErrors((current) => ({ ...current, title: undefined })); }} required aria-invalid={!!errors.title} aria-describedby={errors.title ? "save-title-error" : undefined} className="rounded-xl" autoFocus />
            {errors.title && <p id="save-title-error" data-testid="save-title-error" role="alert" className="mt-1 text-xs text-rose-600">{errors.title}</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <FieldLabel htmlFor="save-city-input" required>City</FieldLabel>
              <Input ref={cityRef} id="save-city-input" data-testid="save-city-input" placeholder="City" value={form.city} required aria-required={true} aria-invalid={!!errors.city} aria-describedby={errors.city ? "save-city-error" : undefined} onChange={(event) => { setForm((current) => ({ ...current, city: event.target.value })); setErrors((current) => ({ ...current, city: undefined })); }} className="rounded-xl" />
              {errors.city && <p id="save-city-error" data-testid="save-city-error" role="alert" className="mt-1 text-xs text-rose-600">{errors.city}</p>}
            </div>
            <div>
              <FieldLabel htmlFor="save-country-input" required>Country</FieldLabel>
              <Input ref={countryRef} id="save-country-input" data-testid="save-country-input" placeholder="Country" value={form.country} required aria-required={true} aria-invalid={!!errors.country} aria-describedby={errors.country ? "save-country-error" : undefined} onChange={(event) => { setForm((current) => ({ ...current, country: event.target.value })); setErrors((current) => ({ ...current, country: undefined })); }} className="rounded-xl" />
              {errors.country && <p id="save-country-error" data-testid="save-country-error" role="alert" className="mt-1 text-xs text-rose-600">{errors.country}</p>}
            </div>
          </div>
          <MapLocation form={form} setForm={setForm} error={errors.map_url} />
          <KeywordsField values={form.keywords} setValues={setKeywords} draft={draft} setDraft={setDraft} inputRef={keywordRef} error={errors.keywords} required={false} />
          <div>
            <FieldLabel htmlFor="save-notes-input">Notes</FieldLabel>
            <Textarea id="save-notes-input" data-testid="save-notes-input" placeholder="Personal notes" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} className="rounded-xl min-h-20" />
          </div>
          <section aria-labelledby="save-cover-label"><p id="save-cover-label" data-testid="save-cover-label" className="text-sm font-bold text-stone-700 mb-2">Cover image</p><ImageUpload value={form.image_path} onChange={(image_path) => setForm((current) => ({ ...current, image_path }))} onUploadStateChange={setUploading} testid="save-image" /></section>
          <DialogFooter className={initialItem ? "flex-row gap-2 sm:space-x-0" : ""}>
            {initialItem && <Button type="button" data-testid="save-edit-cancel" onClick={() => requestClose(onCancel)} disabled={saving} variant="outline" className="rounded-full font-bold flex-1">Cancel</Button>}
            <Button type="submit" data-testid="save-save-btn" disabled={saving || uploading} className={`rounded-full bg-ac hover:bg-ac-hover text-white font-bold ${initialItem ? "flex-1" : "w-full"}`}>{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : initialItem ? "Save changes" : "Save"}</Button>
          </DialogFooter>
        </fieldset>
        {duplicate && <div ref={warningRef} tabIndex={-1} data-testid="save-duplicate-container" aria-label="Duplicate place warning" className="outline-none">
          <DuplicateWarning matches={duplicate.matches} saving={saving} onBack={() => { setDuplicate(null); setErrors({}); requestAnimationFrame(() => titleRef.current?.focus()); }} onConfirm={() => { if (!saving) persist(duplicate.payload, true); }} />
        </div>}
        {errors.save && <p data-testid="save-validation-error" role="alert" className="mt-3 text-sm text-rose-600">{errors.save}</p>}
      </form>
      <AlertDialog open={!!discardAction} onOpenChange={(value) => { if (!value) setDiscardAction(null); }}>
        <AlertDialogContent data-testid="save-unsaved-dialog" className="w-[calc(100%-2rem)] max-w-sm rounded-3xl font-['Figtree']">
          <AlertDialogHeader className="text-left">
            <AlertDialogTitle data-testid="save-unsaved-title" className="font-['Nunito']">Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription data-testid="save-unsaved-description">Your changes haven’t been saved. Keep editing, or discard them and leave this form.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2 sm:space-x-0">
            <AlertDialogCancel data-testid="save-unsaved-keep-editing" className="mt-0 flex-1 rounded-full font-bold">Keep editing</AlertDialogCancel>
            <AlertDialogAction data-testid="save-unsaved-discard" onClick={() => { const close = discardAction; setDiscardAction(null); close?.(); }} className="flex-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white font-bold">Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContent>
  );
});
